import {
  addGarages,
  createGarageAdditionalSettings,
  deleteGarageAdditionalSettings,
  getGarageAdditionalSettings,
  getGarageDetail,
  updateGarageAdditionalSettings,
  updateGarages,
} from 'features/garages/actions';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import { ReactComponent as GarageFilledIcon } from 'icons/garage-filled-icon.svg';
import { ReactComponent as MagnifierIcon } from 'icons/magnifier.svg';
import { ReactComponent as PlusIcon } from 'icons/plus-icon.svg';
import { ReactComponent as DeleteIcon } from 'icons/delete-rounded-icon.svg';
import { Button, InputField, TableWrapper } from 'components/Global';
import { useAppContext } from 'components/Context/AppContext';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import { checkPermission, setCurrency, toPascalCase } from 'utils/functionality';
import { getAllRentalLocation, getExchangeRate } from 'features/rental-location/actions';
import { getServicesByRentalLocation } from 'features/services/actions';
import { trackEvent } from 'utils/analytics';
import NewAdditionalGarageSetting from '../AdditionalGarageSetting/new-index';
import { getAllCategories } from 'features/category/actions';

const NewGarageDetailComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { showToast, setShowConfirmation } = useAppContext();

  const { data, exchangeRate } = useSelector((state) => state.rentalLocation);
  const { garageDetail, garageAdditionalSetting } = useSelector((state) => state.garages);
  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { data: rentalServicesData } = useSelector((state) => state.services);

  const [mainSettingValue, setMainSettingValue] = useState({
    id: 0,
    name: '',
    location_id: 0,
    address_details: '',
    map_link: '',
  });
  const [rentTypeSettingAdd, setRentTypeSettingAdd] = useState([]);
  const [operationalSetting, setOperationalSetting] = useState([]);
  const [rentTypeSettingUpdate, setRentTypeSettingUpdate] = useState([]);
  const [mappedRentTypes, setMappedRentTypes] = useState([]);

  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');

  const garageDetailDataLength = Object.keys(garageDetail).length;

  useEffect(() => {
    if (!id) return;
    dispatch(getGarageDetail(id));
    dispatch(getGarageAdditionalSettings(id));
  }, [id]);

  useEffect(() => {
    dispatch(getAllRentalLocation());
    dispatch(getExchangeRate());
    dispatch(getAllCategories({ page: 1, limit: 0 }));
  }, []);

  useEffect(() => {
    if (!mainSettingValue.location_id) return;
    dispatch(getServicesByRentalLocation(mainSettingValue.location_id));
  }, [mainSettingValue.location_id]);

  useEffect(() => {
    if (!rentalServicesData.length) return;
    setMappedRentTypes(
      rentalServicesData?.[0]?.facilities
        ?.filter?.(
          (item) => item.name === 'With Driver' || item.name === 'Without Driver' || item.name === 'Airport Transfer',
        )
        ?.map((item) => ({
          id: item.id,
          name: item.name,
          service: item.name?.toLowerCase()?.split(' ')?.join('_'),
          value: item.name?.toLowerCase()?.split(' ')?.join('_'),
          outside_operational_status: false,
          outside_operational_fee: '',
          start_time: '',
          end_time: '',
        })),
    );
  }, [rentalServicesData]);

  useEffect(() => {
    if (garageDetailDataLength === 0) return;

    setMainSettingValue({
      ...mainSettingValue,
      name: garageDetail.name,
      location_id: garageDetail.location_id,
      map_link: garageDetail.map_link,
      address_details: garageDetail.address_details,
    });
  }, [garageDetail]);

  useEffect(() => {
    if (garageDetailDataLength === 0) return;
    if (!mappedRentTypes?.length) return;
    setRentTypeSettingUpdate(
      garageDetail.operational
        ?.filter?.(
          (item) =>
            toPascalCase(item.service) === 'With Driver' ||
            toPascalCase(item.service) === 'Without Driver' ||
            toPascalCase(item.service) === 'Airport Transfer',
        )
        ?.map((item, idx) => ({
          id: idx + 1,
          name: toPascalCase(item.service),
          service: mappedRentTypes?.find((service) => service.value === item.service)?.id,
          value: item.service,
          outside_operational_status: item.outside_operational_status,
          outside_operational_fee: item.outside_operational_fee,
          start_time: item.start_time,
          end_time: item.end_time,
        })) ?? [],
    );
  }, [garageDetail, mappedRentTypes]);

  useEffect(() => {
    if (!garageAdditionalSetting || !mappedRentTypes?.length) return;
    setOperationalSetting(
      garageAdditionalSetting?.map((item) => ({
        ...item,
        category_ids: item.categories?.map((item) => ({ ...item, value: item.name.toLowerCase() })),
        service: mappedRentTypes?.find((type) => type.value == item.service)?.id,
        value: mappedRentTypes?.find((type) => type.value === item?.service)?.value,
      })),
    );
  }, [garageAdditionalSetting, mappedRentTypes]);

  useEffect(() => {
    if (!garageDetailDataLength || !mappedRentTypes?.length || !rentTypeSettingUpdate?.length) return;

    const serviceTypeLength = mappedRentTypes?.length || 0;
    const existedOperationalLength = rentTypeSettingUpdate?.length || 0;

    if (serviceTypeLength === existedOperationalLength) return;

    setRentTypeSettingAdd([
      {
        id: 0,
        outside_operational_fee: '',
        outside_operational_status: false,
        service: '',
        start_time: '',
        end_time: '',
      },
    ]);
  }, [garageDetail, rentTypeSettingUpdate, mappedRentTypes]);

  const sendPayloadData = async (action, payload, fulfilledMessage, errorMessage1, withAdditionalSetting) => {
    const filteredSetting = operationalSetting?.filter(
      (item) => item.start_time && item.end_time && item.fee && item.category_ids.length,
    );

    try {
      trackEvent(action === 'ADD' ? 'garage_created' : 'garage_updated', {
        garage_id: action === 'ADD' ? undefined : garageDetail?.id,
        source: 'garage_detail',
      });
      let res;
      if (action === 'ADD') {
        res = await dispatch(addGarages(payload)).unwrap();
      } else if (action === 'UPDATE') {
        await dispatch(updateGarages({ id: garageDetail.id, payload })).unwrap();
      }

      if (withAdditionalSetting) {
        for (const item of filteredSetting) {
          if (item?.added) {
            await dispatch(
              createGarageAdditionalSettings({
                ...item,
                garage_id: action === 'ADD' ? res.id : garageDetail.id,
                fee: +item.fee,
                category_ids: item.category_ids.map((item) => item.id),
                service: mappedRentTypes.find((type) => type.id == item.service)?.value,
              }),
            ).unwrap();
          } else {
            await dispatch(
              updateGarageAdditionalSettings({
                payload: {
                  ...item,
                  fee: +item.fee,
                  category_ids: item.category_ids.map((item) => item.id),
                  service: mappedRentTypes.find((type) => type.id == item.service)?.value,
                },
                id: item.id,
              }),
            ).unwrap();
          }
        }
      }
      showToast({ type: 'success', message: fulfilledMessage });
      navigate(-1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      showToast({ type: 'error', message: `${errorMessage1}: ${error}` });
    }
  };

  const handleKeyDown = (event) => {
    // Prevent the input of commas, dots, and other non-numeric characters
    if (event.key === ',' || event.key === '.' || event.key === 'e' || event.key === '-') {
      event.preventDefault();
    }
  };

  const handleAddGarage = async () => {
    let payload = {
      name: mainSettingValue.name,
      location_id: Number.parseInt(mainSettingValue.location_id),
      address_details: mainSettingValue.address_details,
      map_link: mainSettingValue.map_link,
    };

    const operationalData = [];

    // validation
    const validName = mainSettingValue.name.replace(/\s/).length !== 0;
    const validLocation = mainSettingValue.location_id !== 0;
    const validAddressDetails = mainSettingValue.address_details.replace(/\s/).length !== 0;
    const validMapLink = mainSettingValue.map_link.replace(/\s/).length !== 0;
    let isWithAddtionalSetting = false;

    // check validation
    if (!validName) {
      return showToast({ type: 'error', message: 'Masukan Nama Garasi' });
    } else if (!validLocation) {
      return showToast({ type: 'error', message: 'Pilih Lokasi Rental' });
    } else if (!validMapLink) {
      return showToast({ type: 'error', message: 'Masukan Link Gmaps' });
    } else if (!validAddressDetails) {
      return showToast({ type: 'error', message: 'Masukan Alamat Garasi' });
    }

    // check additional settings validation
    if (rentTypeSettingAdd.length > 0) {
      for (let additionalChild of rentTypeSettingAdd) {
        if (additionalChild.service) {
          if (additionalChild.outside_operational_fee === '')
            return showToast({ type: 'error', message: 'Masukkan Tarif Penalti Setting' });
          if (!additionalChild.start_time) return showToast({ type: 'error', message: 'Masukkan Jam Buka Garasi' });
          if (!additionalChild.end_time) return showToast({ type: 'error', message: 'Masukkan Jam Tutup Garasi' });
          operationalData.push({
            ...additionalChild,
            service: mappedRentTypes?.find((item) => item.id === additionalChild.service)?.value,
            outside_operational_fee: +additionalChild.outside_operational_fee,
          });
        }
      }
    }

    if (rentTypeSettingUpdate.length > 0) {
      for (let additionalChild of rentTypeSettingUpdate) {
        if (additionalChild.service) {
          if (additionalChild.outside_operational_fee === '')
            return showToast({ type: 'error', message: 'Masukkan Tarif Penalti Setting' });
          if (!additionalChild.start_time) return showToast({ type: 'error', message: 'Masukkan Jam Buka Garasi' });
          if (!additionalChild.end_time) return showToast({ type: 'error', message: 'Masukkan Jam Tutup Garasi' });
          operationalData.push({
            ...additionalChild,
            service: mappedRentTypes?.find((item) => item.id === additionalChild.service)?.value,
            outside_operational_fee: +additionalChild.outside_operational_fee,
          });
        }
      }
    }

    const filteredSetting = operationalSetting?.filter(
      (item) => item.start_time && item.end_time && item.fee && item.category_ids.length,
    );

    if (filteredSetting?.length > 0) isWithAddtionalSetting = true;

    // if add garage
    if (garageDetailDataLength === 0) {
      sendPayloadData(
        'ADD',
        { ...payload, operational: operationalData },
        'Berhasil Tambah Garasi',
        'Gagal Tambah Garasi',
        isWithAddtionalSetting,
      );
      return;
    }

    // if update garage
    sendPayloadData(
      'UPDATE',
      { ...payload, operational: operationalData },
      'Berhasil Update Garasi',
      'Gagal Update Garasi',
      isWithAddtionalSetting,
    );
  };

  const handleLocation = (e) => {
    setMainSettingValue((prev) => ({ ...prev, location_id: e.target.id }));
    setRentTypeSettingAdd((prev) =>
      prev?.length > 0
        ? [prev[0]]
        : [
            {
              id: 0,
              outside_operational_fee: '',
              outside_operational_status: false,
              service: '',
              start_time: '',
              end_time: '',
            },
          ],
    );
  };

  const numberInputOnWheelPreventChange = (e) => {
    e.target.blur();
    e.stopPropagation();

    setTimeout(() => {
      e.target.focus();
    }, 0);
  };

  const handleAddRentSetting = () => {
    setRentTypeSettingAdd((prev) => [
      ...prev,
      {
        id: 0,
        outside_operational_fee: '',
        outside_operational_status: false,
        service: '',
        start_time: '',
        end_time: '',
      },
    ]);
  };

  const handleRemoveGarageSetting = async (index, settingId) => {
    if (garageAdditionalSetting?.length > 0 && settingId) {
      setShowConfirmation({
        message: 'Apakah anda yakin ingin menghapus Garasi',
        show: true,
        onClick: async () => {
          try {
            await dispatch(deleteGarageAdditionalSettings(settingId)).unwrap();
            showToast({ type: 'success', message: 'Berhasil Menghapus Pengaturan Garasi' });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
            showToast({ type: 'error', message: 'Gagal Menghapus Pengaturan Garasi' });
          } finally {
            dispatch(getGarageAdditionalSettings(id));
          }
        },
      });
      return;
    }
    setRentTypeSettingAdd((prev) => prev.filter((_, idx) => idx !== index));
  };

  const renderButtonOperationalSetting = (index, item) => {
    const isCreateMode = garageDetailDataLength === 0;
    const isUpdateMode = garageDetailDataLength > 0;
    const rentTypeSettingTotalLength = rentTypeSettingAdd?.length + rentTypeSettingUpdate?.length;
    const isLastOfItem = index === rentTypeSettingAdd?.length - 1;

    if (
      (isUpdateMode && mappedRentTypes?.length === rentTypeSettingTotalLength) ||
      (isCreateMode && isLastOfItem && mappedRentTypes?.length === rentTypeSettingAdd?.length)
    )
      return null;

    if (
      (isLastOfItem && mappedRentTypes?.length !== rentTypeSettingAdd?.length) ||
      (isCreateMode && mappedRentTypes?.length < rentTypeSettingUpdate?.length)
    ) {
      return (
        <div className="garage-detail__divider">
          <Button onClick={handleAddRentSetting} className="garage-detail__add-button" width={26} height={26}>
            <PlusIcon />
          </Button>
        </div>
      );
    } else {
      return (
        <div className="garage-detail__divider">
          <Button
            onClick={() => handleRemoveGarageSetting(index, item.id)}
            className="garage-detail__remove-button"
            width={26}
            height={26}
          >
            <DeleteIcon />
          </Button>
        </div>
      );
    }
  };

  const selectedCountry = data?.find((country) => country.id === +mainSettingValue.location_id);
  const isSingaporeSelected = selectedCountry?.name?.toLowerCase() === 'singapore';

  return (
    <div className="garage-detail">
      <button
        className="detail-setting-harga__header"
        onClick={() => navigate(-1)}
        style={{ border: 'none', background: 'transparent' }}
      >
        <LeftArrow />
        <p>Kembali</p>
      </button>

      <TableWrapper
        title={id ? 'Detail Garasi' : 'Tambah Garasi'}
        icon={<GarageFilledIcon fill="#009EF7" width="25px" height="25px" />}
      >
        <div className="garage-detail__main-setting">
          <h2 className="garage-detail__main-header">Pengaturan Utama</h2>
          <div className="garage-detail__main-setting__form">
            <InputField
              className={'garage-input'}
              label={'Nama Garasi'}
              placeholder={'Masukkan Nama Garasi'}
              value={mainSettingValue.name}
              onChange={(e) => setMainSettingValue((prev) => ({ ...prev, name: e.target.value }))}
              id="nama-garasi"
              name={'nama-garasi'}
            />

            <InputField
              className={'garage-input'}
              label={'Link Gmaps'}
              placeholder={'Masukkan Link'}
              value={mainSettingValue.map_link}
              onChange={(e) => setMainSettingValue((prev) => ({ ...prev, map_link: e.target.value }))}
              id="garasi-map-link"
              name={'map_link'}
            />

            <InputField
              className={'garage-input'}
              label={'Alamat Garasi'}
              placeholder={'Masukkan Alamat Garasi'}
              value={mainSettingValue.address_details}
              onChange={(e) => setMainSettingValue((prev) => ({ ...prev, address_details: e.target.value }))}
              icon={<MagnifierIcon />}
            />

            <SelectFieldDropdown
              label="Lokasi Rental"
              type="select"
              htmlFor="garage-city"
              data={data}
              value={mainSettingValue.location_id}
              placeholder={'Pilih Lokasi'}
              onChange={handleLocation}
            />

            {isSingaporeSelected ? (
              <div className="garage-detail__exchange-rate">
                <p> Nilai Mata Uang:</p>
                <p className="rate">1 SGD = {setCurrency(exchangeRate?.converted_amount ?? 0)?.replace('Rp', 'IDR')}</p>
              </div>
            ) : null}
          </div>
        </div>

        {mappedRentTypes?.length > 0 && (
          <div className="garage-detail__additional-setting">
            {rentTypeSettingUpdate.map((item, idx) => (
              <div key={`additional-update-${item.id}`}>
                <NewAdditionalGarageSetting
                  serviceData={mappedRentTypes}
                  locationId={mainSettingValue.location_id}
                  data={item}
                  setData={setRentTypeSettingUpdate}
                  numberInputOnWheelPreventChange={numberInputOnWheelPreventChange}
                  handleKeyDown={handleKeyDown}
                  index={idx}
                  operationalSetting={operationalSetting?.filter((type) => type.value === item?.value)}
                  setOperationalSetting={setOperationalSetting}
                />
                <div className="garage-detail__divider">
                  <Button
                    onClick={() => handleRemoveGarageSetting(idx, item.id)}
                    className="garage-detail__remove-button"
                    width={26}
                    height={26}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
              </div>
            ))}
            {rentTypeSettingAdd.map((item, idx) => (
              <div key={`additional-add-${idx}`}>
                <NewAdditionalGarageSetting
                  serviceData={mappedRentTypes}
                  locationId={mainSettingValue.location_id}
                  data={item}
                  setData={setRentTypeSettingAdd}
                  numberInputOnWheelPreventChange={numberInputOnWheelPreventChange}
                  handleKeyDown={handleKeyDown}
                  index={idx}
                  operationalSetting={operationalSetting?.filter((type) => type.value === item?.value)}
                  setOperationalSetting={setOperationalSetting}
                />
                {renderButtonOperationalSetting(idx, item)}
              </div>
            ))}
          </div>
        )}

        <div className="garage-detail__buttons">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} width={253} className="garage-btn">
            Kembali
          </Button>
          {(checkPermission(offCanvasMenu, currentMenu, 'update') || garageDetailDataLength === 0) && (
            <Button
              type="button"
              onClick={handleAddGarage}
              width={253}
              className="garage-btn"
              style={{ marginLeft: '10px' }}
            >
              {garageDetailDataLength === 0 ? 'Tambah' : 'Simpan'}
            </Button>
          )}
        </div>
      </TableWrapper>
    </div>
  );
};

export default NewGarageDetailComponent;
